export type OpenNamedPortalTabAfterPromiseOptions = {
  /** Named browsing context; reuses an existing tab with the same name when still open. */
  windowName: string;
  getExistingWindow?: () => Window | null;
  setExistingWindow?: (win: Window | null) => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

/**
 * Opens `about:blank` synchronously in a named tab (preserves user gesture for popup allowlists),
 * then assigns `location` after `resolveUrl` resolves. Reuses an existing tab when available.
 * Omits `noopener` on the synchronous open so `window.opener` remains for storage-transfer postMessage.
 * See `docs/CASE_MGMT_PORTAL_HCP.md` (*Security*, *Popup blockers*).
 */
export function openNamedPortalTabAfterPromise(
  resolveUrl: () => Promise<string>,
  options: OpenNamedPortalTabAfterPromiseOptions
): void {
  const { windowName, getExistingWindow, setExistingWindow, onSuccess, onError } = options;

  const existing = getExistingWindow?.() ?? null;
  if (existing && !existing.closed) {
    void (async () => {
      try {
        const url = await resolveUrl();
        existing.location.href = url;
        existing.focus();
        onSuccess?.();
      } catch (error) {
        onError?.(error);
      }
    })();
    return;
  }

  const placeholderWin = window.open('about:blank', windowName);
  if (placeholderWin) {
    setExistingWindow?.(placeholderWin);
  }

  void (async () => {
    try {
      const url = await resolveUrl();
      if (placeholderWin && !placeholderWin.closed) {
        placeholderWin.location.href = url;
        placeholderWin.focus();
        onSuccess?.();
        return;
      }

      const fallback = window.open(url, windowName);
      if (!fallback) {
        throw new Error('Popup blocked. Allow popups for this site.');
      }
      setExistingWindow?.(fallback);
      fallback.focus();
      onSuccess?.();
    } catch (error) {
      if (placeholderWin && !placeholderWin.closed) {
        placeholderWin.close();
      }
      setExistingWindow?.(null);
      onError?.(error);
    }
  })();
}
